import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';

interface CVViewerModalProps {
  cvId: string;
  candidateName?: string;
  onClose: () => void;
  cachedImages?: string[];
  onImagesLoaded?: (cvId: string, images: string[]) => void;
  onCVNotFound?: (cvId: string) => void;  // Callback when CV no longer exists
}

const CVViewerModal: React.FC<CVViewerModalProps> = ({ cvId, candidateName, onClose, cachedImages, onImagesLoaded, onCVNotFound }) => {
  const { apiCall } = useAuth();
  const [cvImages, setCvImages] = useState<string[]>(cachedImages || []);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(!cachedImages);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);  // Track if CV was deleted/not found

  // Load CV images (skip if cached)
  useEffect(() => {
    if (cachedImages && cachedImages.length > 0) {
      return; // Already have cached images
    }

    const loadCVImages = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await apiCall(`${apiBaseUrl}/api/v1/cv/${cvId}/images`);
        
        if (response.ok) {
          const data = await response.json();
          const images = data.images || [];
          setCvImages(images);
          // Cache the images for future use
          if (onImagesLoaded && images.length > 0) {
            onImagesLoaded(cvId, images);
          }
        } else if (response.status === 404) {
          // CV was deleted or no longer exists
          setIsNotFound(true);
          setError('CV nicht mehr verfügbar');
        } else {
          setError('Failed to load CV images');
        }
      } catch (err: any) {
        console.error('Error loading CV images:', err);
        setError('Failed to load CV images');
      } finally {
        setIsLoading(false);
      }
    };

    if (cvId) {
      loadCVImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId, cachedImages]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Arrow key navigation
  useEffect(() => {
    const handleArrow = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentPage > 0) {
        setCurrentPage(prev => prev - 1);
      }
      if (e.key === 'ArrowRight' && currentPage < cvImages.length - 1) {
        setCurrentPage(prev => prev + 1);
      }
    };
    window.addEventListener('keydown', handleArrow);
    return () => window.removeEventListener('keydown', handleArrow);
  }, [currentPage, cvImages.length]);

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < cvImages.length - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        {/* Header - Just close button */}
        <div className="flex justify-end p-2 border-b bg-slate-50">
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
            title="Close (ESC)"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* CV Image Display */}
        <div className="flex-1 bg-slate-50 overflow-auto min-h-0">
          <div className="w-full flex items-center justify-center p-2 sm:p-4">
            {isLoading ? (
              <div className="w-full max-w-md mx-auto">
                <div className="aspect-[210/297] bg-white rounded-lg border border-slate-200 shadow-sm relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/60 to-transparent z-10" />
                  <div className="p-6 sm:p-8 space-y-4">
                    <div className="h-4 w-2/5 bg-slate-200 rounded" />
                    <div className="space-y-2">
                      <div className="h-2.5 w-full bg-slate-100 rounded" />
                      <div className="h-2.5 w-5/6 bg-slate-100 rounded" />
                      <div className="h-2.5 w-4/6 bg-slate-100 rounded" />
                    </div>
                    <div className="h-3.5 w-1/3 bg-slate-200 rounded mt-4" />
                    <div className="space-y-2">
                      <div className="h-2.5 w-full bg-slate-100 rounded" />
                      <div className="h-2.5 w-4/5 bg-slate-100 rounded" />
                    </div>
                    <div className="h-3.5 w-1/4 bg-slate-200 rounded mt-4" />
                    <div className="space-y-2">
                      <div className="h-2.5 w-full bg-slate-100 rounded" />
                      <div className="h-2.5 w-5/6 bg-slate-100 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-2">⚠️ {error}</p>
                {isNotFound && onCVNotFound ? (
                  <div className="flex flex-col items-center gap-3 mt-4">
                    <p className="text-sm text-gray-500">
                      Diese:r Kandidat:in hat ihren CV aktualisiert oder gelöscht.
                    </p>
                    <button 
                      onClick={() => {
                        onCVNotFound(cvId);
                        onClose();
                      }}
                      className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      🗑️ Aus Gespeicherten entfernen
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={onClose}
                    className="text-sm text-gray-600 hover:text-gray-900 underline"
                  >
                    Close
                  </button>
                )}
              </div>
            ) : cvImages.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <p className="mb-2">📄 No CV images available</p>
                <button 
                  onClick={onClose}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  Close
                </button>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentPage}
                  src={`data:image/png;base64,${cvImages[currentPage]}`}
                  alt={`CV Page ${currentPage + 1}`}
                  className="max-h-[70vh] w-auto object-contain rounded-lg shadow-md"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                />
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* Navigation Footer */}
        {cvImages.length > 1 && !isLoading && !error && (
          <div className="flex justify-between items-center p-2 border-t bg-white">
            <button 
              onClick={handlePrevious}
              disabled={currentPage === 0}
              className={`flex items-center px-3 py-1.5 rounded-lg transition-all ${
                currentPage === 0
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
              title="Previous (←)"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <p className="text-xs font-medium text-gray-500">
              {currentPage + 1} / {cvImages.length}
            </p>
            
            <button 
              onClick={handleNext}
              disabled={currentPage === cvImages.length - 1}
              className={`flex items-center px-3 py-1.5 rounded-lg transition-all ${
                currentPage === cvImages.length - 1
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
              title="Next (→)"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CVViewerModal;

